import { autoUpdate, offset, size } from '@floating-ui/react-dom'
import { Button } from '@components/Button/Button.tsx'
import { AnimatePresence } from 'framer-motion'
import { ReactNode, useRef, useState } from 'react'
import { FaFilter, FaTimes } from 'react-icons/fa'
import {
  ActiveFilters,
  FilterBadge,
  FilterBadgeContainer,
  FilterBadgeText,
  FilterButton,
  FilterSearchContainer,
  Footer,
  HeaderContainer,
  StyledContainer,
  StyledHeader
} from './FilterSelection.styled'
import { FaEraser } from 'react-icons/fa6'
import {
  FloatingFocusManager,
  FloatingPortal,
  useFloating,
  useDismiss,
  useInteractions
} from '@floating-ui/react'
import { useTranslation } from 'react-i18next'
import { deepEqual } from '@root/utils'

export interface IFilterButton {
  id: string
  name: string
  filter: any
}

export interface IFilter extends Record<any, any> {
  [key: string]: any
}

/**
 * Calculates the number of active filters.
 * Counts each filter as 1, and if the filter is an array,
 * counts the length of the array.
 */
const calculateActiveFilters = (filters: IFilter[]): number => {
  return filters.reduce((acc, filter) => {
    return (
      acc +
      Object.entries(filter).reduce((sum, [key, val]) => {
        // The account filter has a list of account types per filter.
        // Need to count each type as a separate filter and not the array's length.
        if (key === 'type') return sum + 1
        return Array.isArray(val) ? sum + val.length : sum + 1
      }, 0)
    )
  }, 0)
}

interface Props {
  filterButtonOptions?: IFilterButton[]
  filterSearchOptions?: ReactNode[]
  filters: Array<IFilter>
  onFilter: (any) => void
}

const FilterSelection: React.FC<Props> = ({
  filterButtonOptions,
  onFilter,
  filterSearchOptions,
  filters
}) => {
  const [t] = useTranslation()
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const numberOfActiveFilters = calculateActiveFilters(filters)
  const isFilterActive = numberOfActiveFilters !== 0

  /* FLOATING */
  const { context, x, y, strategy, refs } = useFloating({
    open: isDropDownOpen,
    onOpenChange: setIsDropDownOpen,
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        layoutShift: false // Prevents micro movement when focus is lost on parent button
      })
      return cleanup
    },
    placement: 'bottom-start',
    middleware: [
      offset({
        mainAxis: 12
      }),
      size({
        apply: ({ availableWidth, elements }) => {
          Object.assign(elements.floating.style, {
            width: `${Math.min(450, availableWidth)}px`
          })
        },
        padding: 24
      })
    ]
  })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  /* FILTER LOGIC */
  const toggleFilter = (filter: IFilter): void => {
    if (filters.some(value => deepEqual(value, filter))) {
      const newState = filters.filter(value => !deepEqual(value, filter))
      onFilter(newState)
    } else {
      const newState = [...filters, filter]
      onFilter(newState)
    }
  }

  const isActive = (filter: IFilter) => {
    return filters.some(value => deepEqual(value, filter)) ? 'active' : 'default'
  }

  const clearSelectedFilters = () => {
    onFilter([])
  }

  return (
    <>
      <FilterButton
        icon={<FaFilter />}
        onClick={() => setIsDropDownOpen(!isDropDownOpen)}
        $active={isFilterActive}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {isFilterActive && <ActiveFilters>{numberOfActiveFilters}</ActiveFilters>}
      </FilterButton>

      <FloatingPortal root={rootRef}>
        <FloatingFocusManager
          context={context}
          returnFocus={true}
          modal={true}
          order={['reference', 'content']}
        >
          <AnimatePresence>
            {isDropDownOpen && (
              <StyledContainer
                ref={refs.setFloating}
                style={{
                  top: y || 0,
                  left: x || 0,
                  position: strategy
                }}
                {...getFloatingProps()}
              >
                <HeaderContainer>
                  <StyledHeader>{t('components.actionBar.filterHeader')}</StyledHeader>
                  <Button
                    icon={<FaTimes />}
                    onClick={() => setIsDropDownOpen(false)}
                    isSecondary={true}
                  ></Button>
                </HeaderContainer>

                <FilterBadgeContainer>
                  {filterButtonOptions?.map(({ id, name, filter }) => (
                    <FilterBadge
                      key={`filter-${id}`}
                      onClick={() => toggleFilter(filter)}
                      initial={false}
                      animate={isActive(filter)}
                    >
                      <FilterBadgeText>{name}</FilterBadgeText>
                    </FilterBadge>
                  ))}
                </FilterBadgeContainer>

                <FilterSearchContainer>
                  {filterSearchOptions?.map(search => search)}
                </FilterSearchContainer>

                <Footer>
                  <Button
                    icon={<FaEraser />}
                    onClick={() => clearSelectedFilters()}
                    disabled={filters.length === 0}
                  >
                    {t('components.actionBar.clearFilters')}
                  </Button>
                </Footer>
              </StyledContainer>
            )}
          </AnimatePresence>
        </FloatingFocusManager>
      </FloatingPortal>
    </>
  )
}

export default FilterSelection
